/**
 * The "Live" card (dashboard and campaign page): who is on a call now and for how long, calls and reached today,
 * outcomes today and the callers on shift.
 *
 * Live from the Dashboards `callcentre` hub (`useCallCentreLive`): the overall board on the dashboard, the campaign's
 * board on a campaign page, plus callers on shift. When the hub is down (or has nothing yet) it falls back to existing
 * REST data refreshed every minute: today's report (calls, reached, outcomes) and the desk phones in use. Who is on a
 * call now is only known live.
 *
 * Live pushes also refresh the page's REST data (debounced): a logged call refreshes the dashboard (or the campaign's
 * counts) and a reminder push refreshes the admin's reminders.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { IconActivity, IconPhoneCall, IconTargetArrow, IconHeadset, IconPhone } from '@tabler/icons-react';
import { Stack, Group, Text, SimpleGrid, Badge, Paper, Tooltip } from '@/components/ui';
import { useCallCentreAdminService, useCallCentreLive, type CallCentreLiveBadgesMessage, type CallCentreLiveBoardMessage } from '@actschurch/ccms-sdk';
import { callCentreKeys, cssColour, dateToYmd } from './callCentreUtils';
import { SectionCard, InlineEmpty, PersonCell } from '../../FormsManagement/components/FormsUi';

const FALLBACK_REFRESH_MS = 60_000;
const REST_REFRESH_DEBOUNCE_MS = 3_000;

/** Re-render every `ms` so "on the phone for 4 min" keeps counting. */
function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/** "Just now", "4 min", "1 h 5 min" since an ISO time. */
function sinceLabel(iso: string, now: number): string {
  const started = Date.parse(iso);
  if (Number.isNaN(started)) return '';
  const minutes = Math.max(0, Math.floor((now - started) / 60_000));
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} h ${m} min` : `${h} h`;
}

/** "08:15" (local time) of an ISO time. */
function clockLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

function LiveNumber({ icon, color, label, value }: { icon: React.ReactNode; color: string; label: string; value: number | null | undefined }) {
  return (
    <Paper withBorder radius="md" p="sm">
      <Group gap="sm" wrap="nowrap">
        <Text c={`${color}.6`} style={{ display: 'flex' }} aria-hidden>
          {icon}
        </Text>
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text size="xl" fw={700} lh={1.2}>
            {value ?? '·'}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {label}
          </Text>
        </Stack>
      </Group>
    </Paper>
  );
}

function ListHeading({ children }: { children: React.ReactNode }) {
  return (
    <Text size="sm" fw={600}>
      {children}
    </Text>
  );
}

export interface CallCentreLiveCardProps {
  /** Follow one campaign's board; leave out for the whole call centre. */
  campaignId?: number;
  helpId?: string;
}

export function CallCentreLiveCard({ campaignId, helpId }: CallCentreLiveCardProps) {
  const admin = useCallCentreAdminService();
  const queryClient = useQueryClient();
  const now = useNow(30_000);

  // A logged call changes the page's REST numbers too: refresh them once things settle.
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
  }, []);
  const onBoard = useCallback(
    (m: CallCentreLiveBoardMessage) => {
      if (m.kind !== 'outcome' || (m.board.campaignID ?? undefined) !== campaignId) return;
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
      refreshTimer.current = setTimeout(() => {
        refreshTimer.current = null;
        void queryClient.invalidateQueries({ queryKey: campaignId ? callCentreKeys.campaign(campaignId) : callCentreKeys.dashboard() });
      }, REST_REFRESH_DEBOUNCE_MS);
    },
    [campaignId, queryClient],
  );
  const onBadges = useCallback(
    (m: CallCentreLiveBadgesMessage) => {
      if (m.kind === 'reminder') void queryClient.invalidateQueries({ queryKey: callCentreKeys.remindersRoot() });
    },
    [queryClient],
  );
  const live = useCallCentreLive({ campaignId: campaignId ?? null, onBoard, onBadges });
  const liveBoard = campaignId ? live.campaignBoard : live.board;
  const boardLive = live.isConnected && !!liveBoard;
  const onlineLive = live.isConnected && !!live.online;

  // Fallbacks (existing REST data), only while the live values are missing.
  const today = dateToYmd(new Date()) ?? '';
  const reportQuery = { from: today, to: today, campaignId };
  const report = useQuery({
    queryKey: callCentreKeys.report({ live: true, ...reportQuery }),
    queryFn: () => admin.getReport(reportQuery),
    enabled: !boardLive,
    refetchInterval: boardLive ? false : FALLBACK_REFRESH_MS,
  });
  const stations = useQuery({
    queryKey: callCentreKeys.stations(),
    queryFn: () => admin.listStations(),
    enabled: !onlineLive,
    refetchInterval: onlineLive ? false : FALLBACK_REFRESH_MS,
  });

  const callsToday = boardLive ? liveBoard!.callsToday : report.data?.totals.calls;
  const reachedToday = boardLive ? liveBoard!.reachedToday : report.data?.totals.reached;
  const outcomes = boardLive
    ? liveBoard!.outcomesToday.map((o) => ({ id: o.outcomeID, name: o.name, color: o.color, count: o.count }))
    : (report.data?.byOutcome ?? []).filter((o) => o.count > 0).map((o) => ({ id: o.outcomeID, name: o.name, color: o.color, count: o.count }));
  const onCall = boardLive ? liveBoard!.onCall : null;
  const onShift = onlineLive
    ? live.online!.callers.map((c) => ({
        key: c.userId,
        name: c.name,
        detail: [c.stationName, c.extension ? `extension ${c.extension}` : null].filter(Boolean).join(', '),
        since: `On since ${clockLabel(c.since)}`,
      }))
    : stations.data
      ? stations.data
          .filter((s) => !!s.inUseByName)
          .map((s) => ({ key: `station-${s.stationID}`, name: s.inUseByName!, detail: `${s.name}, extension ${s.extension}`, since: '' }))
      : null;

  const isLive = boardLive && onlineLive;
  const status = isLive ? (
    <Badge variant="dot" color="green" styles={{ root: { textTransform: 'none' } }}>
      Live
    </Badge>
  ) : (
    <Tooltip label="The live connection is not available right now, so these numbers refresh every minute." multiline w={260} withArrow>
      <Badge variant="dot" color="gray" styles={{ root: { textTransform: 'none' } }}>
        Every minute
      </Badge>
    </Tooltip>
  );

  return (
    <SectionCard
      icon={<IconActivity size={18} />}
      color="green"
      title="Live"
      description={
        campaignId
          ? 'Who is on a call now and how today is going in this campaign.'
          : 'Who is on a call now and how today is going, across every campaign.'
      }
      actions={status}
      helpId={helpId}
    >
      <Stack gap="md">
        <SimpleGrid cols={{ base: 2, md: 4 }}>
          <LiveNumber icon={<IconPhoneCall size={22} />} color="indigo" label="Calls today" value={callsToday} />
          <LiveNumber icon={<IconTargetArrow size={22} />} color="teal" label="Reached today" value={reachedToday} />
          <LiveNumber icon={<IconPhone size={22} />} color="green" label="On a call now" value={onCall?.length} />
          <LiveNumber icon={<IconHeadset size={22} />} color="blue" label="Callers on shift" value={onShift?.length} />
        </SimpleGrid>

        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          <Stack gap="xs">
            <ListHeading>On a call now</ListHeading>
            {onCall === null ? (
              <Text size="sm" c="dimmed">
                Shown when the live connection is up.
              </Text>
            ) : onCall.length === 0 ? (
              <InlineEmpty icon={<IconPhone size={22} />} title="Nobody is on a call" description="Callers show here from the moment they press Call." />
            ) : (
              onCall.map((c) => (
                <Group key={`${c.userId}-${c.campaignID}`} justify="space-between" wrap="nowrap" gap="sm">
                  <PersonCell name={c.name} secondary={!campaignId && c.campaignName ? c.campaignName : undefined} size="sm" avatarKey={c.userId} />
                  <Badge variant="light" color="green" styles={{ root: { textTransform: 'none', flexShrink: 0 } }}>
                    {sinceLabel(c.since, now)}
                  </Badge>
                </Group>
              ))
            )}
          </Stack>

          <Stack gap="xs">
            <ListHeading>Outcomes today</ListHeading>
            {outcomes.length === 0 ? (
              <Text size="sm" c="dimmed">
                {callsToday === undefined ? 'Loading.' : 'No calls logged yet today.'}
              </Text>
            ) : (
              <Group gap={6}>
                {outcomes.map((o) => (
                  <Badge key={o.id} variant="light" color={cssColour(o.color, 'blue')} styles={{ root: { textTransform: 'none' } }}>
                    {o.name}: {o.count}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>

          <Stack gap="xs">
            <ListHeading>Callers on shift</ListHeading>
            {!onlineLive && onShift && (
              <Text size="xs" c="dimmed">
                Desk phones in use only, until the live connection is back.
              </Text>
            )}
            {onShift === null ? (
              <Text size="sm" c="dimmed">
                Loading.
              </Text>
            ) : onShift.length === 0 ? (
              <Text size="sm" c="dimmed">
                Nobody has started a shift.
              </Text>
            ) : (
              onShift.map((c) => (
                <Group key={c.key} justify="space-between" wrap="nowrap" gap="sm">
                  <PersonCell name={c.name} secondary={c.detail || undefined} size="sm" avatarKey={c.key} />
                  {c.since && (
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                      {c.since}
                    </Text>
                  )}
                </Group>
              ))
            )}
          </Stack>
        </SimpleGrid>

      </Stack>
    </SectionCard>
  );
}
